import React, { FC, RefObject, useCallback, useEffect, useMemo } from 'react'
import styles from './PayAccountForm.module.scss'
import { IExtract } from '../../../../modules/consume/domain/models/Extract';
import { PaymentType } from '../../../../modules/consume/domain/models/PaymentType';
import { Button, Icon, IconButton } from '@mui/material';
import { scrooToElement } from '../../../../cross-cutting/ScrollToElement';
import { PaymentSelector } from '../../../catalog/components/paymentTypeSelector/PaymentSelector';
import { PaymnetExtractResume } from './paymentExtractResume/PaymnetExtractResume';
import PartialPaymentOption from '../options/PartialPaymentOption';
import ServiceFeeOption from '../options/ServiceFeeOptionProps';
import ISimplePos from '../../../../modules/simplePos/model/ISimplePos';
import { SimplePosSelector } from '../../../catalog/components/SimplePosSelector/SimplePosSelector';
import Discount, { IDiscountValue } from '../../../catalog/components/discount/Discount';
import { useI18n } from '../../../../hooks/useI18n';
import UseDeviceStore from '../../../../modules/device/store/DeviceStore';

export interface ICloseAccountFormProps {
    //propertys
    onClose?: () => void;
    extract: IExtract;
    onSubmit: (paymentType: PaymentType, amount: number, payServiceFee?: boolean, simplePos?: ISimplePos | null, discount?: IDiscountValue) => void;
    simplePosList?: ISimplePos[];
    enabledDiscount?: boolean;
}

const PayAccountForm: FC<ICloseAccountFormProps> = ({ extract, onClose, onSubmit, simplePosList, enabledDiscount }) => {
    const { t } = useI18n();
    const [deviceConfig] = UseDeviceStore(state => [state.deviceConfig]);
    
    const [simplePos, setSimplePos] = React.useState<ISimplePos | null>(null);
    const [payServiceFee, setPayServiceFee] = React.useState(true);
    const [paymentType, setPaymentType] = React.useState<PaymentType>();
    const [partialAmount, setPartialAmount] = React.useState<number>(payServiceFee ? extract.totalToPayWithFee : extract.totalToPayWithoutFee);
    const [discount, setDiscount] = React.useState<IDiscountValue>();
    const [discountConfig, setDiscountConfig] = React.useState(false);

    const onClickPaymentButtonHandle = useCallback(() => {
        if (!paymentType) return;
        if (partialAmount < 0) return;

        onSubmit(paymentType, partialAmount, payServiceFee, simplePos, discount);

    }, [discount, onSubmit, partialAmount, payServiceFee, paymentType, simplePos]);

    const onCheckPayServiceFeeHandle = useCallback((checked: boolean) => {
        setPartialAmount(checked ? extract.totalToPayWithFee - (discount?.amount ?? 0) : extract.totalToPayWithoutFee - (discount?.amount ?? 0))
        setPayServiceFee(checked);
    }, [discount?.amount, extract.totalToPayWithFee, extract.totalToPayWithoutFee])

    const validate = useMemo(() => {
        if (!paymentType) {
            return false
        }
        if (partialAmount <= 0 && !discount?.amount) {
            return false
        }
        return true
    }, [paymentType, partialAmount, discount?.amount]);


    const totalToPay = useMemo(() => payServiceFee ? extract.totalToPayWithFee : extract.totalToPayWithoutFee, [extract.totalToPayWithFee, extract.totalToPayWithoutFee, payServiceFee])

    const onChangeDiscount = useCallback((value?: IDiscountValue) => {
        if (!value) {
            setDiscount(undefined);
            setPartialAmount(totalToPay);
            return;
        }
        setPartialAmount(partialAmount - value.amount)
        setDiscount(value);
    }, [partialAmount, totalToPay])


    const onFocusInput = useCallback((inputEvent: RefObject<HTMLInputElement>) => {
        scrooToElement(inputEvent);
    }, []);

    if (discountConfig) {
        return (
            <Discount
                total={!payServiceFee ? extract?.totalAmount : extract?.totalAmount + extract.serviceFeeConfig.serviceFeeAmount}
                remaining={totalToPay}
                updateDiscount={onChangeDiscount}
                currentDiscount={discount}
                appliedDiscounts={extract.totalDiscount}
                discountConfig={discountConfig}
                setDiscountConfig={setDiscountConfig}
            />
        )
    }

    return (
        <div id={styles.PayAccountForm} >
            <div className={styles.header} >
                <IconButton onClick={discountConfig ? () => setDiscountConfig(false) : onClose} className={styles.closeButton}><Icon >close</Icon></IconButton>
                <h2>{t('paymentModal.title')}</h2>
            </div>
            <div className={styles.container} >
                <div className={styles.content} >
                    <ServiceFeeOption
                        extract={extract}
                        payServiceFee={payServiceFee}
                        onCheckPayServiceFeeHandle={onCheckPayServiceFeeHandle}
                    />
                    {deviceConfig?.payment.isPaymentDivisorEnabled &&
                        <PartialPaymentOption
                            onFocusInput={onFocusInput}
                            amountToPay={totalToPay - (discount?.amount ?? 0)}
                            extract={extract}
                            total={extract.totalAmount - (discount?.amount ?? 0)}
                            onChange={setPartialAmount}
                        />
                    }

                    <PaymnetExtractResume
                        extract={extract}
                        payServiceFee={payServiceFee}
                        totalToPay={totalToPay}
                        partialAmount={partialAmount}
                        discount={discount?.amount}
                        appliedDiscount={extract.totalDiscount}
                    />
                    {totalToPay > 0 && enabledDiscount && (
                        <Discount
                            total={!payServiceFee ? extract?.totalAmount : extract?.totalAmount + extract.serviceFeeConfig.serviceFeeAmount}
                            remaining={totalToPay}
                            updateDiscount={onChangeDiscount}
                            currentDiscount={discount}
                            appliedDiscounts={extract.totalDiscount}
                            discountConfig={discountConfig}
                            setDiscountConfig={setDiscountConfig}
                        />
                    )}

                    <PaymentSelector
                        paymentType={paymentType}
                        onChangePaymentType={setPaymentType}
                        onFocusInput={onFocusInput}
                        amountToPay={partialAmount}
                        // showCielo={simplePosList && simplePosList.length > 0}
                        showCielo
                    />
                    {
                        paymentType === PaymentType.cielo &&
                        <SimplePosSelector
                            onChangeSimplePos={setSimplePos}
                            selectedSimplePos={simplePos}
                            simplePosList={simplePosList}
                        />
                    }
                </div>
                <div className={styles.footer}>
                    <Button variant={'contained'} disabled={!validate} size='large' color='primary' className={styles.button} onClick={onClickPaymentButtonHandle}>
                        {t('paymentModal.sendPayment')}
                    </Button>
                </div>
            </div>

        </div >
    )
}
export default PayAccountForm


